<template>
    <div class="sink-view view" :class="{ incomplete: !sinkPropertiesVisible }">
        <instructions titleKey="sink" :property="state.productFamily == 'seven' ? 'sinkHelpContent' : 'sinkHelpContentOne'" />

        <worktop-drawing :editableFields="[ 'sinkDistanceLeft', 'sinkDistanceRight', 'sinkDistanceFront' ]" />

        <div class="wrapper">
            <div class="section-content">
                <div class="row">
                    <div class="col-2">
                        <h2 class="h2" v-html="ui.t('sinkChoose')"></h2>

                        <label class="form-group selected-sink">
                            <div class="image-group">
                                <a href="#" class="list-group-item active" :class="{ disabled: !ui.sinkIsValid }" @click.prevent="toggleSinkSelection">
                                    <div class="container">
                                        <div class="top">
                                            <div class="image" :style="{ 'background-image': ui.getBackgroundImageUrl(ui.sink.image!, 200)! }"></div>

                                            <h3>
                                                {{ ui.sink.name }}
                                            </h3>
                                        </div>

                                        <div class="messages" v-if="ui.sinkValidationMessages.length > 0">
                                            <div v-for="message in ui.sinkValidationMessages" :key="message" class="message">
                                                {{ ui.t(message) }}
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            </div>
                        </label>
                    </div>

                    <div class="col-3" v-if="sinkPropertiesVisible">
                        <h2 class="h2" v-html="ui.t('sinkLocation')"></h2>

                        <template v-if="state.sinkDistanceMeasuredTo == 'edge'">
                            <number-input v-model="ui.sinkDistanceLeft"
                                resourceKey="sinkDistanceLeft"
                                @blur="ui.clearFocusedField()"
                                @focus="ui.focusedFieldName = 'sinkDistanceLeft'"
                                :step="5"
                                :max="(ui.worktopLength ?? 0) - ui.sink.length - ui.sink.margins"
                                :min="sinkLeftMargin" />

                            <number-input v-model="ui.sinkDistanceRight"
                                resourceKey="sinkDistanceRight"
                                @blur="ui.clearFocusedField()"
                                @focus="ui.focusedFieldName = 'sinkDistanceRight'"
                                :step="5"
                                :max="(ui.worktopLength ?? 0) - ui.sink.length - ui.sink.margins"
                                :min="sinkRightMargin" />
                        </template>

                        <template v-if="state.sinkDistanceMeasuredTo == 'center'">
                            <number-input v-model="ui.sinkDistanceLeftCenter"
                                resourceKey="sinkDistanceLeft"
                                @blur="ui.clearFocusedField()"
                                @focus="ui.focusedFieldName = 'sinkDistanceLeftCenter'"
                                :step="5"
                                :max="(ui.worktopLength ?? 0) - ui.sink.length - ui.sink.margins + ui.sink.length / 2"
                                :min="sinkLeftMargin" />

                            <number-input v-model="ui.sinkDistanceRightCenter"
                                resourceKey="sinkDistanceRight"
                                @blur="ui.clearFocusedField()"
                                @focus="ui.focusedFieldName = 'sinkDistanceRightCenter'"
                                :step="5"
                                :max="(ui.worktopLength ?? 0) - ui.sink.length - ui.sink.margins + ui.sink.length / 2"
                                :min="sinkRightMargin" />
                        </template>

                        <h2 class="h2" v-html="ui.t('sinkDistanceOrigin')"></h2>

                        <option-list v-model="state.sinkDistanceMeasuredTo" :optionList="sinkMeasurementTypes" />
                    </div>

                    <div class="col-7" v-if="sinkPropertiesVisible">
                        <h2 class="h2" v-html="ui.t('sinkHole')"></h2>

                        <option-list v-model="state.faucetHoleCode" :optionList="ui.faucetHoles" classes="tablet-stacked" />

                        <option-list v-model="state.extraHole" :optionList="ui.extraHoles" classes="tablet-stacked" />

                        <option-list v-model="state.extraHole2" :optionList="ui.extraHoles2" classes="tablet-stacked" />
                    </div>
                </div>
            </div>
        </div>

        <sink-selection-popup v-model:visible="sinkPopupOpen" />

        <div class="navigation">
            <div class="wrapper">
                <router-link :to="`/${state.productFamilyCode}/material`" v-html="ui.t('previous')" />
                <router-link :to="`/${state.productFamilyCode}/hobcutout`" @click.native="moveNext" :class="{ next: true,  disabled: !ui.sinkSelectionsComplete }">
                    <span class="input-tip" v-if="moveNextPrevented" v-html="ui.t('selectionsIncomplete')"></span>
                    <span v-html="ui.t('next')"></span>
                </router-link>
            </div>
        </div>
    </div>
</template>

<script async setup lang="ts">
import { computed, ref } from 'vue';
import { useState, useUI } from '../store';

const sinkPopupOpen = ref(false);
const moveNextPrevented = ref(false);
let moveNextTimer: number | null = null;

const { ui } = useUI();
const { state } = useState();

function moveNext() {
    moveNextPrevented.value = true;

    moveNextTimer = setTimeout(() => moveNextPrevented.value = false, 3000);
}

const sinkLeftMargin = computed(() => {
    let margin = ui.sink.margins;

    if (state.sink == 'E' && (state.faucetHole == 'L' || state.extraHole == 'L' || state.extraHole2 == 'L'))
        margin = 110;

    if (state.sink == 'N' && ui.worktopWidth <= 500 && (state.faucetHole == 'L' || state.extraHole == 'L' || state.extraHole2 == 'L'))
        margin = 90;

    // Functional area requires 225mm from edge
    if (state.functionalAreaCode == 'L' && margin < 225)
        margin = 225;
    else if (state.functionalAreaCode == "R" && margin < 45)
        margin = 45;

    if (state.sinkDistanceMeasuredTo == 'center')
        margin += ui.sink.length / 2;

    return margin;
});

const sinkRightMargin = computed(() => {
    let margin = ui.sink.margins;

    if (state.sink == 'E' && (state.faucetHole == 'R' || state.extraHole == 'R' || state.extraHole2 == 'R'))
        margin = 110;

    if (state.sink == 'N' && ui.worktopWidth <= 500 && (state.faucetHole == 'R' || state.extraHole == 'R' || state.extraHole2 == 'R'))
        margin = 90;

    // Functional area requires 225mm from edge
    if (state.functionalAreaCode == 'R' && margin < 225)
        margin = 225;   
    else if (state.functionalAreaCode == "L" && margin < 45)
        margin = 45;

    if (state.sinkDistanceMeasuredTo == 'center')
        margin += ui.sink.length / 2;

    return margin;
});

const sinkMeasurementTypes = computed(() => {
    return {
        title: null,
        options: [
            {
                value: "edge",
                text: ui.t("sinkToEdge"),
                image: null,
                embedMedia: null,
                link: null,
                disabled: false,
                visible: true,
                warnings: [],
                messages: []
            },
            {
                value: "center",
                text: ui.t("sinkToCenter"),
                image: null,
                embedMedia: null,
                link: null,
                disabled: false,
                visible: true,
                warnings: [],
                messages: []
            }
        ]
    }
});

function created() {
    if (!state.sink)
        toggleSinkSelection();
}

function toggleSinkSelection() {
    sinkPopupOpen.value = !sinkPopupOpen.value;
}

const sinkPropertiesVisible = computed(() => {
    return ui.sinkIsValid && state.sink && ui.sink.length > 0;
});

created();

</script>